import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { CartAddEvent, CartUpdateEvent } from '@theme/events';
import { isMobileBreakpoint } from '@theme/utilities';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {AbortController | null} */
  #historyAbortController = null;

  #cartRefreshSequence = 0;
  #refreshCartOnOpen = async () => {
    const sequence=++this.#cartRefreshSequence;
    const dialog=this.refs.dialog;
    if(!dialog)return;
    dialog.setAttribute('aria-busy','true');
    let status=this.querySelector('[data-kp-cart-refresh]');
    if(!status){status=document.createElement('p');status.dataset.kpCartRefresh='';status.setAttribute('role','status');status.setAttribute('aria-live','polite');status.style.cssText='margin:12px;padding:12px;color:#26315c;background:#eef2ff;border-radius:12px;font-size:14px;line-height:1.5;';dialog.prepend(status);}
    status.replaceChildren();status.textContent='Refreshing your cart…';
    try{
      const response=await fetch((window.Shopify?.routes?.root||'/')+'cart.js',{cache:'no-store',headers:{Accept:'application/json'},signal:AbortSignal.timeout(15000)});
      if(!response.ok)throw Error('Cart read failed');
      const cart=await response.json();
      if(sequence!==this.#cartRefreshSequence||!this.isConnected)return;
      if(!Array.isArray(cart.items)||!Number.isSafeInteger(cart.item_count)||cart.item_count<0)throw Error('Invalid cart response');
      document.dispatchEvent(new CartUpdateEvent(cart,'kippush-cart-open',{source:'kippush-cart-open',itemCount:cart.item_count}));
      status.remove();
    }catch(error){
      if(sequence!==this.#cartRefreshSequence||!this.isConnected)return;
      status.textContent='We could not refresh your cart. Your saved items have not been changed. ';
      const retry=document.createElement('button');retry.type='button';retry.textContent='Retry';retry.style.cssText='min-height:44px;margin-left:8px;';retry.addEventListener('click',this.#refreshCartOnOpen);status.append(retry);
      const link=document.createElement('a');link.href=(window.Shopify?.routes?.root||'/')+'cart';link.textContent='View full cart';link.style.cssText='display:inline-flex;align-items:center;min-height:44px;margin-left:12px;';status.append(link);
    }finally{if(sequence===this.#cartRefreshSequence)dialog.removeAttribute('aria-busy');}
  };

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.addEventListener(DialogOpenEvent.eventName, this.#refreshCartOnOpen);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);

    if (history.state?.cartDrawerOpen) {
      history.replaceState(null, '');
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.removeEventListener(DialogOpenEvent.eventName, this.#refreshCartOnOpen);
    this.#cartRefreshSequence++;
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    this.#historyAbortController?.abort();
  }

  #handleHistoryOpen = () => {
    if (!isMobileBreakpoint()) return;

    if (!history.state?.cartDrawerOpen) {
      history.pushState({ cartDrawerOpen: true }, '');
    }

    this.#historyAbortController = new AbortController();
    window.addEventListener('popstate', this.#handlePopState, { signal: this.#historyAbortController.signal });
  };

  #handleHistoryClose = () => {
    this.#historyAbortController?.abort();
    if (history.state?.cartDrawerOpen) {
      history.back();
    }
  };

  #handlePopState = async () => {
    if (this.refs.dialog?.open) {
      this.refs.dialog.style.setProperty('--dialog-drawer-closing-animation', 'none');
      await this.closeDialog();
      this.refs.dialog.style.removeProperty('--dialog-drawer-closing-animation');
    }
  };

  #handleCartAdd = () => {
    if (this.hasAttribute('auto-open') && document.body.dataset.kpNonapparel !== 'true') {
      this.showDialog();
    }
  };

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}

